import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  PaperPlaneTilt,
  MagnifyingGlass,
  ChatCircleDots,
  ArrowLeft,
  CheckCircle,
  Check,
  Plus,
  X,
  Image as ImageIcon,
} from "@phosphor-icons/react";

// Prefix an /api/... path with the backend origin so <img src> loads cross-origin
// in local dev too. Absolute URLs are returned as-is.
const resolveMedia = (u) => {
  if (!u) return u;
  if (/^https?:\/\//i.test(u)) return u;
  return `${BACKEND_URL}${u}`;
};

/**
 * Messages page — 1-to-1 private messaging.
 *
 * Layout: master-detail. On desktop the conversation list is a permanent
 * left column; on mobile the list becomes full-screen and the thread opens
 * as a route (/messages/:peerId).
 *
 * Polls /api/dm/summary every 8s and /api/dm/messages every 4s when a
 * conversation is open. Simple, no websocket dependency required.
 */

const POLL_LIST_MS = 8000;
const POLL_THREAD_MS = 4000;

function fmtHM(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function fmtRelative(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return fmtHM(iso);
    const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
    if (diffDays === 1) return "Hier";
    if (diffDays < 7) return d.toLocaleDateString("fr-FR", { weekday: "short" });
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
  } catch {
    return "";
  }
}

function initials(name) {
  return (name || "?")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export default function Messages() {
  const { peerId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [q, setQ] = useState("");
  const [newRecipientOpen, setNewRecipientOpen] = useState(false);
  const [pendingImage, setPendingImage] = useState(null); // { file, previewUrl }
  const [uploading, setUploading] = useState(false);
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);

  const usersById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  const fetchThreads = () => api.get("/dm/summary").then((r) => setThreads(r.data || []));
  const fetchUsers = () => api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
  const fetchMessages = (pid) =>
    pid && api.get(`/dm/messages?peer_id=${pid}`).then((r) => setMessages(r.data || []));

  // Initial + list polling
  useEffect(() => {
    fetchThreads();
    fetchUsers();
    const id = setInterval(fetchThreads, POLL_LIST_MS);
    return () => clearInterval(id);
  }, []);

  // Thread polling when a conversation is open
  useEffect(() => {
    if (!peerId) { setMessages([]); return; }
    fetchMessages(peerId);
    const id = setInterval(() => fetchMessages(peerId), POLL_THREAD_MS);
    return () => clearInterval(id);
  }, [peerId]);

  // Autoscroll on new messages
  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, peerId]);

  const pickImage = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // let the user re-pick the same file later
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Seules les images sont autorisées");
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast.error("Image trop volumineuse (max 15 Mo)");
      return;
    }
    setPendingImage({ file: f, previewUrl: URL.createObjectURL(f) });
  };

  const clearPendingImage = () => {
    if (pendingImage?.previewUrl) URL.revokeObjectURL(pendingImage.previewUrl);
    setPendingImage(null);
  };

  const send = async () => {
    const t = text.trim();
    if ((!t && !pendingImage) || !peerId || sending) return;
    setSending(true);
    const snapshotText = t;
    const snapshotImage = pendingImage;
    setText("");
    try {
      let image_url = null;
      if (snapshotImage) {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", snapshotImage.file);
        const up = await api.post("/dm/upload-image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        image_url = up.data?.image_url;
        setUploading(false);
      }
      clearPendingImage();
      await api.post("/dm", { to_id: peerId, text: snapshotText, image_url });
      await fetchMessages(peerId);
      await fetchThreads();
    } catch (err) {
      toast.error(formatApiError(err));
      setText(snapshotText);
      setPendingImage(snapshotImage);
    } finally {
      setUploading(false);
      setSending(false);
    }
  };

  const filteredThreads = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return threads;
    return threads.filter((t) => {
      const u = usersById[t.peer_id];
      const name = (u?.name || "").toLowerCase();
      return name.includes(s) || (t.last?.text || "").toLowerCase().includes(s);
    });
  }, [threads, usersById, q]);

  const peer = peerId ? usersById[peerId] : null;
  const availableUsers = users.filter((u) => u.id !== user?.id);
  const showListOnMobile = !peerId;

  return (
    <div className="flex h-[calc(100vh-64px)] md:h-[calc(100vh-72px)] bg-[#0A0A0A]" data-testid="messages-page">
      {/* --- Conversation list --- */}
      <aside
        className={`${showListOnMobile ? "flex" : "hidden md:flex"} flex-col w-full md:w-80 md:shrink-0 md:border-r md:border-white/10 bg-gradient-to-b from-[#0F0F0F] via-[#0A0A0A] to-[#0A0A0A]`}
      >
        <div className="p-3 flex items-center gap-2 border-b border-white/5">
          <div className="w-9 h-9 rounded-xl bg-[#005CFF]/20 border border-[#005CFF]/30 flex items-center justify-center shadow-[0_0_18px_rgba(0,92,255,0.35)]">
            <ChatCircleDots size={18} className="text-[#7CB2FF]" weight="duotone" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-sm">Messages</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">
              {threads.length} conversation{threads.length > 1 ? "s" : ""}
            </div>
          </div>
          <button
            onClick={() => setNewRecipientOpen((v) => !v)}
            className="w-9 h-9 rounded-xl bg-[#005CFF] hover:bg-[#0047CC] flex items-center justify-center transition-colors shadow-[0_0_20px_rgba(0,92,255,0.4)]"
            data-testid="dm-new-btn"
            title="Nouvelle conversation"
          >
            <PaperPlaneTilt size={14} weight="bold" className="text-white" />
          </button>
        </div>

        <div className="relative px-3 py-2">
          <MagnifyingGlass size={13} className="absolute left-6 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher…"
            className="bg-white/5 border-white/10 h-9 pl-8 text-sm"
            data-testid="dm-search"
          />
        </div>

        {newRecipientOpen && (
          <div className="mx-3 mb-2 rounded-xl border border-[#005CFF]/30 bg-[#005CFF]/[0.08] p-2 max-h-56 overflow-y-auto" data-testid="dm-recipient-list">
            <div className="text-[10px] uppercase tracking-widest text-[#7CB2FF] pb-1 px-1">Nouveau destinataire</div>
            {availableUsers.length === 0 ? (
              <div className="text-xs text-white/50 p-2">Aucun autre utilisateur</div>
            ) : (
              availableUsers.map((u) => (
                <button
                  key={u.id}
                  onClick={() => { setNewRecipientOpen(false); nav(`/messages/${u.id}`); }}
                  className="w-full flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-white/10 transition-colors text-left"
                  data-testid={`dm-recipient-${u.id}`}
                >
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold shrink-0" style={{ background: (u.color || "#005CFF") + "33", color: u.color || "#7CB2FF" }}>
                    {initials(u.name)}
                  </div>
                  <div className="flex-1 min-w-0 text-xs truncate">{u.name}</div>
                  <span className="text-[9px] uppercase tracking-widest text-white/40">{u.role}</span>
                </button>
              ))
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-1">
          {filteredThreads.length === 0 && !newRecipientOpen ? (
            <div className="text-center text-white/50 text-xs py-8 px-4">
              Aucune conversation.<br />
              <button onClick={() => setNewRecipientOpen(true)} className="text-[#7CB2FF] underline mt-2">
                Démarrer une conversation
              </button>
            </div>
          ) : (
            filteredThreads.map((t) => {
              const u = usersById[t.peer_id];
              const active = t.peer_id === peerId;
              const iSent = t.last?.from_id === user?.id;
              return (
                <button
                  key={t.peer_id}
                  onClick={() => nav(`/messages/${t.peer_id}`)}
                  className={`w-full flex items-center gap-2.5 rounded-xl p-2.5 transition-all text-left ${
                    active
                      ? "bg-gradient-to-r from-[#005CFF]/25 to-transparent border border-[#005CFF]/40 shadow-[0_0_18px_rgba(0,92,255,0.15)]"
                      : "hover:bg-white/[0.04] border border-transparent"
                  }`}
                  data-testid={`dm-thread-${t.peer_id}`}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-xs font-bold shrink-0 ring-1"
                    style={{
                      background: `linear-gradient(135deg, ${(u?.color || "#005CFF")}40, ${(u?.color || "#005CFF")}10)`,
                      color: u?.color || "#7CB2FF",
                      "--tw-ring-color": `${(u?.color || "#005CFF")}40`,
                    }}
                  >
                    {initials(u?.name || t.peer_id.slice(0, 2))}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <div className="text-sm font-medium truncate">{u?.name || "Utilisateur"}</div>
                      <div className="text-[10px] text-white/40 ml-auto shrink-0 font-mono">
                        {fmtRelative(t.last?.created_at)}
                      </div>
                    </div>
                    <div className="text-[11px] text-white/55 truncate flex items-center gap-1">
                      {iSent && (
                        t.last?.read
                          ? <CheckCircle size={11} weight="fill" className="text-[#7CB2FF] shrink-0" />
                          : <Check size={11} className="text-white/40 shrink-0" />
                      )}
                      {t.last?.has_media && !t.last?.text ? "📎 pièce jointe" : (t.last?.text || "…")}
                    </div>
                  </div>
                  {t.unread > 0 && (
                    <div
                      className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#005CFF] text-white text-[10px] font-bold flex items-center justify-center shadow-[0_0_12px_rgba(0,92,255,0.6)] shrink-0"
                      data-testid={`dm-thread-${t.peer_id}-unread`}
                    >
                      {t.unread > 99 ? "99+" : t.unread}
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>
      </aside>

      {/* --- Thread view --- */}
      <main className={`${showListOnMobile ? "hidden md:flex" : "flex"} flex-1 min-w-0 flex-col relative`} data-testid="dm-thread-view">
        {!peerId ? (
          <div className="flex-1 flex items-center justify-center text-center px-6">
            <div>
              <div className="w-16 h-16 rounded-2xl bg-[#005CFF]/15 border border-[#005CFF]/30 flex items-center justify-center mx-auto mb-4">
                <ChatCircleDots size={32} className="text-[#7CB2FF]" weight="duotone" />
              </div>
              <div className="font-display font-bold text-lg">Aucune conversation ouverte</div>
              <div className="text-xs text-white/50 mt-1">Sélectionne une conversation à gauche.</div>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-white/5 bg-gradient-to-b from-white/[0.04] to-transparent backdrop-blur">
              <button
                onClick={() => nav("/messages")}
                className="md:hidden w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center"
                data-testid="dm-back"
                aria-label="Retour"
              >
                <ArrowLeft size={14} />
              </button>
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-xs font-bold shrink-0 ring-1"
                style={{
                  background: `linear-gradient(135deg, ${(peer?.color || "#005CFF")}40, ${(peer?.color || "#005CFF")}10)`,
                  color: peer?.color || "#7CB2FF",
                  "--tw-ring-color": `${(peer?.color || "#005CFF")}40`,
                }}
              >
                {initials(peer?.name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate">{peer?.name || "Utilisateur"}</div>
                <div className="text-[10px] uppercase tracking-widest text-white/40">{peer?.role || ""}</div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-3 md:px-6 py-4 space-y-2" data-testid="dm-messages">
              {messages.length === 0 ? (
                <div className="text-center text-white/40 text-xs py-8">Aucun message. Envoie le premier 👋</div>
              ) : (
                messages.map((m) => {
                  const mine = m.from_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm relative ${
                        mine
                          ? "bg-gradient-to-br from-[#005CFF] to-[#0047CC] text-white shadow-[0_4px_16px_-4px_rgba(0,92,255,0.5)]"
                          : "bg-white/[0.06] border border-white/10 text-white"
                      }`}>
                        {!mine && (
                          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-0.5">
                            {m.from_name || "Utilisateur"}
                          </div>
                        )}
                        {m.image_url && (
                          <a href={resolveMedia(m.image_url)} target="_blank" rel="noreferrer">
                            <img
                              src={resolveMedia(m.image_url)}
                              alt=""
                              className="rounded-lg mb-1 max-h-64 object-cover cursor-zoom-in"
                              data-testid={`dm-msg-image-${m.id}`}
                            />
                          </a>
                        )}
                        {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
                        <div className={`text-[9px] mt-1 tracking-wider tabular-nums flex items-center gap-1 ${mine ? "text-white/70 justify-end" : "text-white/40"}`}>
                          <span>{fmtHM(m.created_at)}</span>
                          {mine && (
                            (m.read_by || []).includes(peerId)
                              ? <CheckCircle size={10} weight="fill" />
                              : <Check size={10} />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={bottomRef} />
            </div>

            {/* Composer */}
            <div className="p-3 border-t border-white/5 bg-gradient-to-t from-[#0F0F0F] to-[#0A0A0A]">
              {pendingImage && (
                <div className="mb-2 relative inline-block" data-testid="dm-pending-image">
                  <img
                    src={pendingImage.previewUrl}
                    alt=""
                    className="h-24 rounded-lg border border-white/10 object-cover"
                  />
                  <button
                    onClick={clearPendingImage}
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-black/80 border border-white/20 flex items-center justify-center hover:bg-red-500 transition-colors"
                    data-testid="dm-pending-image-remove"
                    aria-label="Retirer l'image"
                  >
                    <X size={12} />
                  </button>
                  <div className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/70 text-[9px] uppercase tracking-widest text-white/70 flex items-center gap-1">
                    <ImageIcon size={10} weight="duotone" /> prête
                  </div>
                </div>
              )}
              <div className="flex items-end gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={pickImage}
                  className="hidden"
                  data-testid="dm-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending || uploading}
                  className="h-11 w-11 rounded-md p-0 shrink-0 flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white transition-colors disabled:opacity-40"
                  data-testid="dm-attach-btn"
                  aria-label="Ajouter une photo"
                  title="Ajouter une photo"
                >
                  <Plus size={18} weight="bold" />
                </button>
                <Input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                  }}
                  placeholder={pendingImage ? "Ajouter une légende…" : "Écrire un message…"}
                  className="bg-white/5 border-white/10 h-11 text-sm"
                  data-testid="dm-input"
                  disabled={sending}
                />
                <Button
                  onClick={send}
                  disabled={sending || uploading || (!text.trim() && !pendingImage)}
                  className="h-11 w-11 p-0 bg-[#005CFF] hover:bg-[#0047CC] shrink-0 shadow-[0_0_20px_rgba(0,92,255,0.4)] disabled:opacity-40 disabled:shadow-none"
                  data-testid="dm-send"
                  aria-label="Envoyer"
                >
                  <PaperPlaneTilt size={16} weight="fill" />
                </Button>
              </div>
              {uploading && (
                <div className="text-[10px] uppercase tracking-widest text-[#7CB2FF] mt-1.5" data-testid="dm-uploading">
                  Envoi de l&apos;image…
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
